// Nutrition Store - Phase 2: State Management Evolution
import { create } from 'zustand';
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware';
import { UnifiedNutritionService, NutritionGoal, NutritionDailySummary, Meal } from '@/services/nutrition/UnifiedNutritionService';

interface NutritionState {
  // Current data
  goals: NutritionGoal | null;
  dailySummary: NutritionDailySummary | null;
  todaysMeals: Meal[];
  
  // UI state
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
  
  // Cache
  summaryCache: Map<string, NutritionDailySummary>;
  mealsCache: Map<string, Meal[]>;
}

interface NutritionActions {
  // Data actions
  setGoals: (goals: NutritionGoal | null) => void;
  setDailySummary: (summary: NutritionDailySummary | null) => void;
  setTodaysMeals: (meals: Meal[]) => void;
  addMeal: (meal: Meal) => void;
  removeMeal: (mealId: string) => void;
  updateMeal: (mealId: string, updates: Partial<Meal>) => void;
  
  // Cache actions
  cacheSummary: (date: string, summary: NutritionDailySummary) => void;
  cacheMeals: (date: string, meals: Meal[]) => void;
  getCachedSummary: (date: string) => NutritionDailySummary | null;
  getCachedMeals: (date: string) => Meal[] | null;
  clearCache: () => void;
  
  // Loading actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Async actions
  refreshTodaysData: (userId: string) => Promise<void>;
  refreshGoals: (userId: string) => Promise<void>;
  isStale: () => boolean;
  
  // Reset
  reset: () => void;
}

type NutritionStore = NutritionState & NutritionActions;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 30; // Keep last 30 days

export const useNutritionStore = create<NutritionStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          // Initial state
          goals: null,
          dailySummary: null,
          todaysMeals: [],
          loading: false,
          error: null,
          lastUpdated: null,
          summaryCache: new Map(),
          mealsCache: new Map(),

          // Data actions
          setGoals: (goals) => {
            set({ goals, lastUpdated: Date.now() });
          },

          setDailySummary: (dailySummary) => {
            set({ dailySummary, lastUpdated: Date.now() });
            
            // Cache the summary
            if (dailySummary) {
              get().cacheSummary(dailySummary.date, dailySummary);
            }
          },

          setTodaysMeals: (todaysMeals) => {
            set({ todaysMeals, lastUpdated: Date.now() });
            
            // Cache today's meals
            const today = new Date().toISOString().split('T')[0];
            get().cacheMeals(today, todaysMeals);
          },

          addMeal: (meal) => {
            set((state) => ({
              todaysMeals: [...state.todaysMeals, meal],
              lastUpdated: Date.now()
            }));
          },

          removeMeal: (mealId) => {
            set((state) => ({
              todaysMeals: state.todaysMeals.filter(meal => meal.id !== mealId),
              lastUpdated: Date.now()
            }));
          },

          updateMeal: (mealId, updates) => {
            set((state) => ({
              todaysMeals: state.todaysMeals.map(meal =>
                meal.id === mealId ? { ...meal, ...updates } : meal
              ),
              lastUpdated: Date.now()
            }));
          },

          // Cache actions
          cacheSummary: (date, summary) => {
            set((state) => {
              const newCache = new Map(state.summaryCache);
              newCache.set(date, summary);
              
              // Limit cache size
              if (newCache.size > MAX_CACHE_SIZE) {
                const oldestKey = newCache.keys().next().value;
                newCache.delete(oldestKey);
              }
              
              return { summaryCache: newCache };
            });
          },

          cacheMeals: (date, meals) => {
            set((state) => {
              const newCache = new Map(state.mealsCache);
              newCache.set(date, meals);
              
              // Limit cache size
              if (newCache.size > MAX_CACHE_SIZE) {
                const oldestKey = newCache.keys().next().value;
                newCache.delete(oldestKey);
              }
              
              return { mealsCache: newCache };
            });
          },

          getCachedSummary: (date) => {
            return get().summaryCache.get(date) || null;
          },

          getCachedMeals: (date) => {
            return get().mealsCache.get(date) || null;
          },

          clearCache: () => {
            set({
              summaryCache: new Map(),
              mealsCache: new Map()
            });
          },

          // Loading actions
          setLoading: (loading) => {
            set({ loading });
          },

          setError: (error) => {
            set({ error, loading: false });
          },

          // Async actions
          refreshTodaysData: async (userId: string) => {
            try {
              set({ loading: true, error: null });
              
              const today = new Date().toISOString().split('T')[0];
              
              // Check cache first
              const cachedSummary = get().getCachedSummary(today);
              const cachedMeals = get().getCachedMeals(today);
              
              if (cachedSummary && cachedMeals && !get().isStale()) {
                set({
                  dailySummary: cachedSummary,
                  todaysMeals: cachedMeals,
                  loading: false
                });
                return;
              }
              
              // Fetch fresh data
              const [summaryResponse, mealsResponse] = await Promise.all([
                UnifiedNutritionService.getDailySummary(userId, today),
                UnifiedNutritionService.getMeals(userId, today, today)
              ]);
              
              if (summaryResponse.success) {
                get().setDailySummary(summaryResponse.data);
              }
              
              if (mealsResponse.success) {
                get().setTodaysMeals(mealsResponse.data || []);
              }
              
              set({ loading: false });
              
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Failed to refresh nutrition data';
              set({ error: errorMessage, loading: false });
            }
          },

          refreshGoals: async (userId: string) => {
            try {
              const response = await UnifiedNutritionService.getCurrentGoals(userId);
              
              if (response.success) {
                get().setGoals(response.data);
              } else {
                set({ error: response.error });
              }
              
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Failed to refresh goals';
              set({ error: errorMessage });
            }
          },

          isStale: () => {
            const { lastUpdated } = get();
            if (!lastUpdated) return true;
            return Date.now() - lastUpdated > CACHE_DURATION;
          },

          // Reset
          reset: () => {
            set({
              goals: null,
              dailySummary: null,
              todaysMeals: [],
              loading: false,
              error: null,
              lastUpdated: null,
              summaryCache: new Map(),
              mealsCache: new Map()
            });
          }
        }),
        {
          name: 'nutrition-store',
          partialize: (state) => ({
            goals: state.goals,
            dailySummary: state.dailySummary,
            summaryCache: Array.from(state.summaryCache.entries()),
            mealsCache: Array.from(state.mealsCache.entries()),
            lastUpdated: state.lastUpdated
          }),
          onRehydrateStorage: () => (state) => {
            if (state) {
              // Restore Maps from arrays
              state.summaryCache = new Map(state.summaryCache || []);
              state.mealsCache = new Map(state.mealsCache || []);
            }
          }
         }
       )
       ),
     { name: 'nutrition-store' }
   )
 );

// Selector hooks for performance
export const useNutritionGoals = () => useNutritionStore((state) => state.goals);
export const useDailySummary = () => useNutritionStore((state) => state.dailySummary);
export const useTodaysMeals = () => useNutritionStore((state) => state.todaysMeals);
export const useNutritionLoading = () => useNutritionStore((state) => state.loading);
export const useNutritionError = () => useNutritionStore((state) => state.error);
export const useNutritionActions = () => useNutritionStore((state) => ({
  refreshTodaysData: state.refreshTodaysData,
  refreshGoals: state.refreshGoals,
  addMeal: state.addMeal,
  removeMeal: state.removeMeal,
  updateMeal: state.updateMeal,
  setError: state.setError,
  reset: state.reset
}));